import { FmeaStandard } from '../types';

export interface FmeaColumnConfig {
    key: string;
    header: string;
    width?: number;
    isNumeric?: boolean;
    isDropdown?: boolean;
    isMandatory?: boolean;
    description?: string;
}

export interface RiskLevel {
    name: string;
    color: 'red' | 'yellow' | 'green' | 'orange';
    bgClass: string;
    textClass: string;
    borderClass: string;
    description: string;
}

export interface RiskMatrixConfig {
    // Axis configuration
    xAxis: { key: 'O' | 'D'; label: string; description: string; scale: string[] };
    yAxis: { key: 'S'; label: string; description: string; scale: string[] };

    // Risk levels for this standard (ordered from highest to lowest risk)
    levels: RiskLevel[];

    // Function to determine risk level from ratings
    // Returns index into levels array (0 = highest risk, levels.length-1 = lowest)
    getRiskLevel: (severity: number, occurrence: number, detection: number) => number;

    // Matrix titles
    primaryTitle: string;
    secondaryTitle?: string;

    // Show secondary matrix (S x D)?
    showSecondaryMatrix: boolean;

    // Risk threshold explanation
    thresholdInfo: string;
}

export interface FmeaStandardConfig {
    name: string;
    steps: string[];
    columns: FmeaColumnConfig[];
    riskModel: 'RPN' | 'AP';
    complianceRules: string[];
    riskMatrix: RiskMatrixConfig;
}

// ============================================================================
// RISK LEVEL DEFINITIONS BY STANDARD
// ============================================================================

const AIAG_VDA_LEVELS: RiskLevel[] = [
    { name: 'High (H)', color: 'red', bgClass: 'bg-red-100', textClass: 'text-red-800', borderClass: 'border-red-300', description: 'Action Required - High risk of failure reaching customer' },
    { name: 'Medium (M)', color: 'yellow', bgClass: 'bg-yellow-100', textClass: 'text-yellow-800', borderClass: 'border-yellow-300', description: 'Optimization Recommended - Moderate risk' },
    { name: 'Low (L)', color: 'green', bgClass: 'bg-green-100', textClass: 'text-green-800', borderClass: 'border-green-300', description: 'Acceptable - Low risk, no action required' },
];

const RPN_LEVELS: RiskLevel[] = [
    { name: 'Critical', color: 'red', bgClass: 'bg-red-200', textClass: 'text-red-900', borderClass: 'border-red-400', description: 'RPN > 200: Immediate action required' },
    { name: 'High', color: 'orange', bgClass: 'bg-orange-100', textClass: 'text-orange-800', borderClass: 'border-orange-300', description: 'RPN 120-200: Action plan required' },
    { name: 'Medium', color: 'yellow', bgClass: 'bg-yellow-100', textClass: 'text-yellow-800', borderClass: 'border-yellow-300', description: 'RPN 70-119: Action recommended' },
    { name: 'Low', color: 'green', bgClass: 'bg-green-100', textClass: 'text-green-800', borderClass: 'border-green-300', description: 'RPN < 70: Acceptable risk' },
];

const ISO_14971_LEVELS: RiskLevel[] = [
    { name: 'Unacceptable', color: 'red', bgClass: 'bg-red-200', textClass: 'text-red-900', borderClass: 'border-red-400', description: 'Risk is unacceptable - Must be reduced' },
    { name: 'ALARP', color: 'yellow', bgClass: 'bg-yellow-100', textClass: 'text-yellow-800', borderClass: 'border-yellow-300', description: 'As Low As Reasonably Practicable - Benefit-risk analysis required' },
    { name: 'Broadly Acceptable', color: 'green', bgClass: 'bg-green-100', textClass: 'text-green-800', borderClass: 'border-green-300', description: 'Risk is acceptable without further reduction' },
];

const MIL_STD_LEVELS: RiskLevel[] = [
    { name: 'Cat I - Catastrophic', color: 'red', bgClass: 'bg-red-300', textClass: 'text-red-900', borderClass: 'border-red-500', description: 'Death, loss of system, or severe damage' },
    { name: 'Cat II - Critical', color: 'orange', bgClass: 'bg-orange-200', textClass: 'text-orange-900', borderClass: 'border-orange-400', description: 'Severe injury, major damage, mission loss' },
    { name: 'Cat III - Marginal', color: 'yellow', bgClass: 'bg-yellow-100', textClass: 'text-yellow-800', borderClass: 'border-yellow-300', description: 'Minor injury, minor damage, mission degradation' },
    { name: 'Cat IV - Negligible', color: 'green', bgClass: 'bg-green-100', textClass: 'text-green-800', borderClass: 'border-green-300', description: 'Less than minor injury/damage' },
];

const ISO_26262_LEVELS: RiskLevel[] = [
    { name: 'ASIL D', color: 'red', bgClass: 'bg-red-300', textClass: 'text-red-900', borderClass: 'border-red-500', description: 'Highest integrity - DC ≥ 99%, HFT = 2' },
    { name: 'ASIL C', color: 'orange', bgClass: 'bg-orange-200', textClass: 'text-orange-900', borderClass: 'border-orange-400', description: 'High integrity - DC ≥ 97%, HFT = 1' },
    { name: 'ASIL B', color: 'yellow', bgClass: 'bg-yellow-200', textClass: 'text-yellow-900', borderClass: 'border-yellow-400', description: 'Medium integrity - DC ≥ 90%' },
    { name: 'ASIL A', color: 'yellow', bgClass: 'bg-yellow-100', textClass: 'text-yellow-800', borderClass: 'border-yellow-300', description: 'Low integrity - DC ≥ 60%' },
    { name: 'QM', color: 'green', bgClass: 'bg-green-100', textClass: 'text-green-800', borderClass: 'border-green-300', description: 'Quality Management - No safety requirement' },
];

const IEC_61508_LEVELS: RiskLevel[] = [
    { name: 'SIL 4', color: 'red', bgClass: 'bg-red-300', textClass: 'text-red-900', borderClass: 'border-red-500', description: 'PFD < 10⁻⁴, SFF ≥ 99%' },
    { name: 'SIL 3', color: 'orange', bgClass: 'bg-orange-200', textClass: 'text-orange-900', borderClass: 'border-orange-400', description: 'PFD < 10⁻³, SFF ≥ 97%' },
    { name: 'SIL 2', color: 'yellow', bgClass: 'bg-yellow-200', textClass: 'text-yellow-900', borderClass: 'border-yellow-400', description: 'PFD < 10⁻², SFF ≥ 90%' },
    { name: 'SIL 1', color: 'yellow', bgClass: 'bg-yellow-100', textClass: 'text-yellow-800', borderClass: 'border-yellow-300', description: 'PFD < 10⁻¹, SFF ≥ 60%' },
    { name: 'Non-SIL', color: 'green', bgClass: 'bg-green-100', textClass: 'text-green-800', borderClass: 'border-green-300', description: 'No safety integrity requirement' },
];

// ============================================================================
// RISK CALCULATION FUNCTIONS
// ============================================================================

function aiagVdaRiskLevel(s: number, o: number, d: number): number {
    // AIAG-VDA Action Priority logic
    if (s >= 9) {
        if (o >= 6) return 0; // High
        if (o >= 4) return d <= 4 ? 1 : 0;
        if (o >= 2) return d <= 1 ? 2 : 1;
        return 2;
    }
    if (s >= 7) {
        if (o >= 6) return 0;
        if (o >= 4) return d <= 4 ? 1 : 0;
        if (o >= 2) return d <= 4 ? 2 : 1;
        return 2;
    }
    if (s >= 4) {
        if (o >= 8) return 0;
        if (o >= 6) return d <= 4 ? 1 : 0;
        if (o >= 4) return d <= 4 ? 2 : 1;
        return 2;
    }
    if (o >= 8) return 1;
    if (o >= 6) return d <= 4 ? 2 : 1;
    return 2;
}

function rpnRiskLevel(s: number, o: number, d: number): number {
    const rpn = s * o * d;
    if (s >= 9 || rpn > 200) return 0; // Critical
    if (rpn >= 120) return 1; // High
    if (rpn >= 70) return 2; // Medium
    return 3; // Low
}

function iso14971RiskLevel(s: number, p1: number, p2: number): number {
    // ISO 14971: S = Severity, P1 = Prob of hazard, P2 = Prob of harm
    // Risk = Severity × (P1 × P2)
    const prob = (p1 * p2) / 25; // Normalize to 0-1
    const risk = s * prob;
    if (risk >= 1.2 || (s >= 4 && prob >= 0.3)) return 0; // Unacceptable - use risk variable
    if (s >= 3 || prob >= 0.5) return 1; // ALARP
    return 2; // Acceptable
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function milStdRiskLevel(s: number, _a: number, _b: number): number {
    // MIL-STD-1629A: Primarily severity-based (Categories I-IV)
    // Note: _a and _b are required by function signature but not used in this simplified implementation
    if (s >= 9) return 0; // Cat I
    if (s >= 7) return 1; // Cat II
    if (s >= 4) return 2; // Cat III
    return 3; // Cat IV
}

function iso26262RiskLevel(s: number, e: number, c: number): number {
    // ISO 26262: ASIL from S (0-3), E (0-4), C (0-3)
    // Simplified mapping
    const score = s + e + c;
    if (score >= 8) return 0; // ASIL D
    if (score >= 6) return 1; // ASIL C
    if (score >= 4) return 2; // ASIL B
    if (score >= 2) return 3; // ASIL A
    return 4; // QM
}

function iec61508RiskLevel(c: number, f: number, p: number): number {
    // IEC 61508: SIL from Consequence, Frequency, Possibility of avoidance
    const score = c + f + p;
    if (score >= 8) return 0; // SIL 4
    if (score >= 6) return 1; // SIL 3
    if (score >= 4) return 2; // SIL 2
    if (score >= 2) return 3; // SIL 1
    return 4; // Non-SIL
}

// ============================================================================
// STANDARDS CONFIGURATION
// ============================================================================

export const STANDARDS_CONFIG: Record<FmeaStandard, FmeaStandardConfig> = {
    AIAG_VDA: {
        name: 'AIAG-VDA 2019',
        steps: [
            '1. Planning & Preparation',
            '2. Structure Analysis',
            '3. Function Analysis',
            '4. Failure Analysis',
            '5. Risk Analysis',
            '6. Optimization',
            '7. Results Documentation'
        ],
        riskModel: 'AP',
        columns: [
            { key: 'id', header: 'ID', width: 100 },
            { key: 'function', header: 'Next Higher Level', width: 180, isDropdown: true },
            { key: 'itemFunction', header: 'Focus Element', width: 180, isDropdown: true },
            { key: 'requirement', header: 'Requirement', width: 200, isMandatory: true },
            { key: 'failureMode', header: 'Failure Mode (FM)', width: 200, isDropdown: true },
            { key: 'effects', header: 'Failure Effects (FE)', width: 250, isDropdown: true },
            { key: 'S', header: 'S', width: 50, isNumeric: true },
            { key: 'specialCharacteristic', header: 'Class', width: 70, isDropdown: true, description: 'SC/CC Classification (AIAG-VDA)' },
            { key: 'O', header: 'O', width: 50, isNumeric: true },
            { key: 'D', header: 'D', width: 50, isNumeric: true },
            { key: 'actionPriority', header: 'AP', width: 60 },
            { key: 'prevControls', header: 'Prevention (PC)', width: 220 },
            { key: 'detControls', header: 'Detection (DC)', width: 220 },
            { key: 'recommendedAction', header: 'Recommended Action', width: 250 },
            { key: 'actionResponsibility', header: 'Responsibility', width: 150 },
            { key: 'actionTargetDate', header: 'Target Date', width: 120 },
            { key: 'actionTaken', header: 'Action Taken', width: 250 },
            { key: 'actionCompletionDate', header: 'Completion Date', width: 120 }
        ],
        complianceRules: [
            'Linkage from Structure to Function required',
            'Severity >= 9 requires high-level action',
            'AP "High" requires optimization'
        ],
        riskMatrix: {
            xAxis: { key: 'O', label: 'Occurrence (O)', description: 'Likelihood of cause occurring', scale: ['Remote', 'Low', 'Moderate', 'High', 'Very High'] },
            yAxis: { key: 'S', label: 'Severity (S)', description: 'Severity of effect on customer', scale: ['None', 'Minor', 'Low', 'Moderate', 'High', 'Significant', 'Major', 'Extreme', 'Hazardous w/Warning', 'Hazardous w/o Warning'] },
            levels: AIAG_VDA_LEVELS,
            getRiskLevel: aiagVdaRiskLevel,
            primaryTitle: 'Action Priority Matrix (S × O)',
            secondaryTitle: 'Detectability Matrix (S × D)',
            showSecondaryMatrix: true,
            thresholdInfo: 'H = Immediate action | M = Action recommended | L = No action needed'
        }
    },
    AIAG_V4: {
        name: 'AIAG 4th Edition',
        steps: [
            'Step 1: Preparation',
            'Step 2: Analysis',
            'Step 3: Risk Assessment',
            'Step 4: Recommended Actions',
            'Step 5: Completion'
        ],
        riskModel: 'RPN',
        columns: [
            { key: 'id', header: 'ID', width: 100 },
            { key: 'function', header: 'Core Function', width: 180, isDropdown: true },
            { key: 'itemFunction', header: 'Component', width: 180, isDropdown: true },
            { key: 'failureMode', header: 'Failure Mode', width: 200, isDropdown: true },
            { key: 'effects', header: 'Potential Effects', width: 250, isDropdown: true },
            { key: 'S', header: 'S', width: 50, isNumeric: true },
            { key: 'specialCharacteristic', header: 'Class', width: 70, isDropdown: true, description: 'SC/CC Classification' },
            { key: 'O', header: 'O', width: 50, isNumeric: true },
            { key: 'D', header: 'D', width: 50, isNumeric: true },
            { key: 'actionPriority', header: 'Class', width: 60 },
            { key: 'prevControls', header: 'Current Prev Controls', width: 220 },
            { key: 'detControls', header: 'Current Det Controls', width: 220 },
            { key: 'recommendedAction', header: 'Recommended Action', width: 250 },
            { key: 'actionResponsibility', header: 'Responsibility', width: 150 },
            { key: 'actionTargetDate', header: 'Target Date', width: 120 },
            { key: 'actionTaken', header: 'Action Taken', width: 250 },
            { key: 'actionCompletionDate', header: 'Completion Date', width: 120 }
        ],
        complianceRules: [
            'RPN > 100 requires attention',
            'Severity >= 8 requires critical review'
        ],
        riskMatrix: {
            xAxis: { key: 'O', label: 'Occurrence (O)', description: 'Probability of failure cause (1-10)', scale: ['1: Remote', '2-3: Low', '4-5: Moderate', '6-7: High', '8-10: Very High'] },
            yAxis: { key: 'S', label: 'Severity (S)', description: 'Severity of failure effect (1-10)', scale: ['1: None', '2-3: Minor', '4-5: Low', '6-7: Moderate', '8: High', '9-10: Hazardous'] },
            levels: RPN_LEVELS,
            getRiskLevel: rpnRiskLevel,
            primaryTitle: 'Risk Priority Number (S × O × D)',
            secondaryTitle: 'Severity vs Detection (S × D)',
            showSecondaryMatrix: true,
            thresholdInfo: 'Critical: RPN > 200 | High: RPN 120-200 | Medium: RPN 70-119 | Low: RPN < 70'
        }
    },
    SAE_J1739: {
        name: 'SAE J1739',
        steps: [
            '1. Planning',
            '2. Functional Analysis',
            '3. Failure Analysis',
            '4. Risk Assessment',
            '5. Recommended Actions'
        ],
        riskModel: 'RPN',
        columns: [
            { key: 'id', header: 'ID', width: 100 },
            { key: 'function', header: 'Item/Function', width: 180, isDropdown: true },
            { key: 'itemFunction', header: 'Requirement', width: 180, isDropdown: true },
            { key: 'failureMode', header: 'Potential Failure Mode', width: 200, isDropdown: true },
            { key: 'effects', header: 'Potential Effects of Failure', width: 250, isDropdown: true },
            { key: 'S', header: 'S', width: 50, isNumeric: true },
            { key: 'specialCharacteristic', header: 'Class', width: 70, isDropdown: true, description: 'SC/CC Classification' },
            { key: 'O', header: 'O', width: 50, isNumeric: true },
            { key: 'D', header: 'D', width: 50, isNumeric: true },
            { key: 'prevControls', header: 'Current Prevention Controls', width: 220 },
            { key: 'detControls', header: 'Current Detection Controls', width: 220 }
        ],
        complianceRules: ['RPN threshold target: 100'],
        riskMatrix: {
            xAxis: { key: 'O', label: 'Occurrence (O)', description: 'Probability of cause (1-10)', scale: ['1-2: Unlikely', '3-4: Remote', '5-6: Occasional', '7-8: Frequent', '9-10: High'] },
            yAxis: { key: 'S', label: 'Severity (S)', description: 'Effect severity (1-10)', scale: ['1: None', '2-4: Minor', '5-6: Moderate', '7-8: Major', '9-10: Hazardous'] },
            levels: RPN_LEVELS,
            getRiskLevel: rpnRiskLevel,
            primaryTitle: 'RPN Risk Matrix',
            secondaryTitle: 'Detection Analysis',
            showSecondaryMatrix: true,
            thresholdInfo: 'Target RPN < 100 | High severity (≥9) always requires action'
        }
    },
    ISO_14971: {
        name: 'ISO 14971 (Medical)',
        steps: [
            '1. Risk Analysis',
            '2. Risk Evaluation',
            '3. Risk Control',
            '4. Residual Risk Evaluation',
            '5. Risk Management Report'
        ],
        riskModel: 'AP',
        columns: [
            { key: 'id', header: 'Hazard ID', width: 100 },
            { key: 'function', header: 'Hazardous Situation', width: 200, isDropdown: true },
            { key: 'itemFunction', header: 'Harm (P1 of Harm)', width: 180, isDropdown: true },
            { key: 'failureMode', header: 'Cause / Sequence of Events', width: 220, isDropdown: true },
            { key: 'effects', header: 'Harm Description', width: 250, isDropdown: true },
            { key: 'S', header: 'Sev', width: 50, isNumeric: true, description: 'Severity of Harm (1-5)' },
            { key: 'O', header: 'P1', width: 50, isNumeric: true, description: 'Probability of Hazard (1-5)' },
            { key: 'D', header: 'P2', width: 50, isNumeric: true, description: 'Probability of Harm (1-5)' },
            { key: 'actionPriority', header: 'Risk Level', width: 90, description: 'Acceptable/ALARP/Unacceptable' },
            { key: 'prevControls', header: 'Risk Control Measure', width: 220 },
            { key: 'detControls', header: 'Verification of Effectiveness', width: 220 }
        ],
        complianceRules: [
            'Risk must be reduced to acceptable level per ISO 14971',
            'Benefit-Risk analysis required for residual risk',
            'Risk control measures must be verified',
            'Documentation of risk acceptability criteria required'
        ],
        riskMatrix: {
            xAxis: { key: 'O', label: 'Probability (P1 × P2)', description: 'Combined probability of harm occurring', scale: ['Improbable', 'Remote', 'Occasional', 'Probable', 'Frequent'] },
            yAxis: { key: 'S', label: 'Severity of Harm', description: 'Severity to patient/user', scale: ['Negligible', 'Minor', 'Serious', 'Critical', 'Catastrophic'] },
            levels: ISO_14971_LEVELS,
            getRiskLevel: iso14971RiskLevel,
            primaryTitle: 'ISO 14971 Risk Matrix (Severity × Probability)',
            showSecondaryMatrix: false,
            thresholdInfo: 'Red = Unacceptable | Yellow = ALARP (benefit-risk required) | Green = Broadly Acceptable'
        }
    },
    MIL_STD_1629A: {
        name: 'MIL-STD-1629A (Aerospace)',
        steps: [
            '1. Ground Rules & Assumptions',
            '2. System Block Diagram',
            '3. Failure Mode Identification',
            '4. Criticality Analysis (CA)',
            '5. Maintainability Analysis',
            '6. FMECA Report'
        ],
        riskModel: 'AP',
        columns: [
            { key: 'id', header: 'FM ID', width: 100 },
            { key: 'function', header: 'Item/Nomenclature', width: 180, isDropdown: true },
            { key: 'itemFunction', header: 'Function', width: 180, isDropdown: true },
            { key: 'failureMode', header: 'Failure Mode & Cause', width: 220, isDropdown: true },
            { key: 'effects', header: 'Local/Next/End Effect', width: 280, isDropdown: true },
            { key: 'S', header: 'Sev', width: 50, isNumeric: true, description: 'Severity Cat (I-IV)' },
            { key: 'O', header: 'α', width: 50, isNumeric: true, description: 'Mode Ratio (0-1)' },
            { key: 'D', header: 'β', width: 50, isNumeric: true, description: 'Cond. Prob (0-1)' },
            { key: 'actionPriority', header: 'Cm', width: 70, description: 'Criticality Number' },
            { key: 'prevControls', header: 'Compensating Provisions', width: 220 },
            { key: 'detControls', header: 'Detection Method', width: 220 }
        ],
        complianceRules: [
            'Severity I = Catastrophic (loss of life)',
            'Severity II = Critical (major injury/damage)',
            'Severity III = Marginal (minor injury)',
            'Severity IV = Negligible (no injury)',
            'Single point failures shall be identified',
            'Criticality Number Cm = βαλpt (Task 102)'
        ],
        riskMatrix: {
            xAxis: { key: 'O', label: 'Failure Probability (α×λ)', description: 'Failure mode probability', scale: ['Improbable', 'Remote', 'Occasional', 'Reasonably Probable', 'Frequent'] },
            yAxis: { key: 'S', label: 'Severity Category', description: 'Effect severity classification', scale: ['IV: Negligible', 'III: Marginal', 'II: Critical', 'I: Catastrophic', ''] },
            levels: MIL_STD_LEVELS,
            getRiskLevel: milStdRiskLevel,
            primaryTitle: 'Criticality Matrix (Severity × Probability)',
            showSecondaryMatrix: false,
            thresholdInfo: 'Cat I (Red) = Catastrophic | Cat II (Orange) = Critical | Cat III (Yellow) = Marginal | Cat IV (Green) = Negligible'
        }
    },
    ISO_26262: {
        name: 'ISO 26262 (Automotive Safety)',
        steps: [
            '1. Hazard Analysis & Risk Assessment',
            '2. Safety Goal Definition',
            '3. Functional Safety Concept',
            '4. Technical Safety Concept',
            '5. System/HW/SW Design',
            '6. Verification & Validation',
            '7. Safety Assessment'
        ],
        riskModel: 'AP',
        columns: [
            { key: 'id', header: 'ID', width: 100 },
            { key: 'function', header: 'Safety Goal', width: 180, isDropdown: true },
            { key: 'itemFunction', header: 'Safety Function', width: 180, isDropdown: true },
            { key: 'requirement', header: 'Safety Requirement', width: 200, isMandatory: true },
            { key: 'failureMode', header: 'Fault/Failure', width: 200, isDropdown: true },
            { key: 'effects', header: 'Hazard Effect', width: 250, isDropdown: true },
            { key: 'S', header: 'S', width: 50, isNumeric: true, description: 'Severity (S0-S3)' },
            { key: 'O', header: 'E', width: 50, isNumeric: true, description: 'Exposure (E0-E4)' },
            { key: 'D', header: 'C', width: 50, isNumeric: true, description: 'Controllability (C0-C3)' },
            { key: 'actionPriority', header: 'ASIL', width: 70, description: 'QM, A, B, C, D' },
            { key: 'prevControls', header: 'Safety Mechanism', width: 220 },
            { key: 'detControls', header: 'Fault Detection', width: 220 }
        ],
        complianceRules: [
            'ASIL D requires highest diagnostic coverage (DC ≥ 99%)',
            'Single point faults not allowed for ASIL C/D',
            'Hardware Architectural Metrics: SPFM, LFM required',
            'ISO 26262-5 Part 5 Hardware Development'
        ],
        riskMatrix: {
            xAxis: { key: 'O', label: 'Exposure (E)', description: 'Duration of exposure to hazard', scale: ['E0: Incredible', 'E1: Very Low', 'E2: Low', 'E3: Medium', 'E4: High'] },
            yAxis: { key: 'S', label: 'Severity (S)', description: 'Potential injuries', scale: ['S0: None', 'S1: Light', 'S2: Severe', 'S3: Life-threatening+', ''] },
            levels: ISO_26262_LEVELS,
            getRiskLevel: iso26262RiskLevel,
            primaryTitle: 'ASIL Determination Matrix (S × E × C)',
            showSecondaryMatrix: false,
            thresholdInfo: 'ASIL D = Highest | ASIL C = High | ASIL B = Medium | ASIL A = Low | QM = No safety'
        }
    },
    IEC_61508: {
        name: 'IEC 61508 (Industrial Safety)',
        steps: [
            '1. Concept & Scope Definition',
            '2. Hazard & Risk Analysis',
            '3. Safety Requirements Specification',
            '4. SIL Determination',
            '5. Design & Development',
            '6. Verification & Validation',
            '7. Functional Safety Assessment'
        ],
        riskModel: 'AP',
        columns: [
            { key: 'id', header: 'ID', width: 100 },
            { key: 'function', header: 'Safety Function', width: 180, isDropdown: true },
            { key: 'itemFunction', header: 'Sub-Function', width: 180, isDropdown: true },
            { key: 'requirement', header: 'Safety Requirement', width: 200, isMandatory: true },
            { key: 'failureMode', header: 'Dangerous Failure', width: 200, isDropdown: true },
            { key: 'effects', header: 'Hazardous Event', width: 250, isDropdown: true },
            { key: 'S', header: 'C', width: 50, isNumeric: true, description: 'Consequence (C1-C4)' },
            { key: 'O', header: 'F', width: 50, isNumeric: true, description: 'Frequency (F1-F2)' },
            { key: 'D', header: 'P', width: 50, isNumeric: true, description: 'Possibility of Avoidance (P1-P2)' },
            { key: 'actionPriority', header: 'SIL', width: 60, description: 'SIL 1-4' },
            { key: 'prevControls', header: 'Risk Reduction Measure', width: 220 },
            { key: 'detControls', header: 'Diagnostic Test', width: 220 }
        ],
        complianceRules: [
            'SIL 3/4 requires redundancy and diagnostics',
            'Safe Failure Fraction (SFF) requirements',
            'Hardware Fault Tolerance (HFT) per SIL level',
            'Proof test intervals must be defined'
        ],
        riskMatrix: {
            xAxis: { key: 'O', label: 'Frequency (F) + Avoidance (P)', description: 'Exposure and avoidance probability', scale: ['F1+P1: Rare/Possible', 'F1+P2: Rare/Unlikely', 'F2+P1: Frequent/Possible', 'F2+P2: Frequent/Unlikely', ''] },
            yAxis: { key: 'S', label: 'Consequence (C)', description: 'Severity of hazardous event', scale: ['C1: Minor', 'C2: Serious', 'C3: Extensive', 'C4: Catastrophic', ''] },
            levels: IEC_61508_LEVELS,
            getRiskLevel: iec61508RiskLevel,
            primaryTitle: 'SIL Determination Matrix (C × F × P)',
            showSecondaryMatrix: false,
            thresholdInfo: 'SIL 4 = Highest (Red) | SIL 3 (Orange) | SIL 2-1 (Yellow) | Non-SIL (Green)'
        }
    },
    CUSTOMER_CUSTOM: {
        name: 'Customer Specific',
        steps: ['1. Initiation', '2. Analysis', '3. Approval'],
        riskModel: 'RPN',
        columns: [
            { key: 'id', header: 'ID', width: 100 },
            { key: 'function', header: 'Process/Item', width: 180, isDropdown: true },
            { key: 'itemFunction', header: 'Function/Req', width: 180, isDropdown: true },
            { key: 'failureMode', header: 'Failure Mode', width: 200, isDropdown: true },
            { key: 'effects', header: 'Failure Effects', width: 250, isDropdown: true },
            { key: 'S', header: 'S', width: 50, isNumeric: true },
            { key: 'O', header: 'O', width: 50, isNumeric: true },
            { key: 'D', header: 'D', width: 50, isNumeric: true },
            { key: 'prevControls', header: 'Controls', width: 220 }
        ],
        complianceRules: [],
        riskMatrix: {
            xAxis: { key: 'O', label: 'Occurrence', description: 'Likelihood of failure', scale: ['Rare', 'Low', 'Medium', 'High', 'Very High'] },
            yAxis: { key: 'S', label: 'Severity', description: 'Impact of failure', scale: ['Minor', 'Moderate', 'Major', 'Critical', 'Catastrophic'] },
            levels: RPN_LEVELS,
            getRiskLevel: rpnRiskLevel,
            primaryTitle: 'Risk Priority Matrix',
            showSecondaryMatrix: true,
            thresholdInfo: 'Customize thresholds as needed'
        }
    }
};

// ============================================================================
// STEP-TO-NAVIGATION LINK MAPPING
// Maps each methodology step to relevant app pages for quick navigation
// ============================================================================

export interface StepQuickLink {
    label: string;
    path: string;
    icon?: string; // Lucide icon name
    description?: string;
}

export interface WorkflowStep {
    stepNumber: number;
    title: string;
    description: string;
    quickLinks: StepQuickLink[];
}

export type StandardWorkflow = Record<string, WorkflowStep[]>;

export const STANDARD_WORKFLOWS: Record<FmeaStandard, WorkflowStep[]> = {
    AIAG_VDA: [
        {
            stepNumber: 1, title: 'Planning & Preparation', description: 'Define scope, team, and project boundaries', quickLinks: [
                { label: 'Project Setup', path: '/projects', icon: 'Settings', description: 'Configure project metadata' },
                { label: 'Customer Requirements', path: '/customer-requirements', icon: 'FileText', description: 'Define VOC inputs' },
            ]
        },
        {
            stepNumber: 2, title: 'Structure Analysis', description: 'Build system hierarchy and boundaries', quickLinks: [
                { label: 'P-Diagrams', path: '/p-diagram', icon: 'Network', description: 'Parameter diagrams' },
                { label: 'BOM Structure', path: '/bom-functions', icon: 'List', description: 'Bill of Materials' },
            ]
        },
        {
            stepNumber: 3, title: 'Function Analysis', description: 'Identify functions and requirements', quickLinks: [
                { label: 'P-Diagrams', path: '/p-diagram', icon: 'Network', description: 'Function definition' },
                { label: 'Traceability', path: '/traceability-matrix', icon: 'GitBranch', description: 'Requirements linkage' },
            ]
        },
        {
            stepNumber: 4, title: 'Failure Analysis', description: 'Identify failure modes, causes, and effects', quickLinks: [
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle', description: 'Full FMEA worksheet' },
                { label: 'Failure Chains', path: '/graph-view', icon: 'Link', description: 'Cause-effect chains' },
            ]
        },
        {
            stepNumber: 5, title: 'Risk Analysis', description: 'Evaluate S/O/D and Action Priority', quickLinks: [
                { label: 'Risk Matrix', path: '/risk-analysis', icon: 'Grid', description: 'AP matrix view' },
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle', description: 'Rating entry' },
            ]
        },
        {
            stepNumber: 6, title: 'Optimization', description: 'Define and track corrective actions', quickLinks: [
                { label: 'Action Tracker', path: '/action-tracking', icon: 'CheckSquare', description: 'Track mitigations' },
                { label: 'DVP&R', path: '/dvpr', icon: 'ClipboardCheck', description: 'Validation plan' },
            ]
        },
        {
            stepNumber: 7, title: 'Results Documentation', description: 'Generate reports and archive', quickLinks: [
                { label: 'Reports', path: '/export', icon: 'FileBarChart', description: 'Generate outputs' },
                { label: 'Epoch History', path: '/epoch-manager', icon: 'History', description: 'Version archive' },
            ]
        },
    ],
    AIAG_V4: [
        {
            stepNumber: 1, title: 'Preparation', description: 'Form team, define scope, gather data', quickLinks: [
                { label: 'Project Setup', path: '/project-management', icon: 'Settings' },
                { label: 'Customer Reqs', path: '/customer-requirements', icon: 'FileText' },
            ]
        },
        {
            stepNumber: 2, title: 'Analysis', description: 'Identify functions, failures, causes, effects', quickLinks: [
                { label: 'P-Diagrams', path: '/p-diagrams', icon: 'Network' },
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle' },
            ]
        },
        {
            stepNumber: 3, title: 'Risk Assessment', description: 'Rate S, O, D and calculate RPN', quickLinks: [
                { label: 'Risk Matrix', path: '/risk-matrix', icon: 'Grid' },
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle' },
            ]
        },
        {
            stepNumber: 4, title: 'Recommended Actions', description: 'Define mitigations for high-RPN items', quickLinks: [
                { label: 'Action Tracker', path: '/action-tracking', icon: 'CheckSquare' },
                { label: 'DVP&R', path: '/dvpr', icon: 'ClipboardCheck' },
            ]
        },
        {
            stepNumber: 5, title: 'Completion', description: 'Document results and close out', quickLinks: [
                { label: 'Reports', path: '/reports', icon: 'FileBarChart' },
                { label: 'Epoch History', path: '/epochs', icon: 'History' },
            ]
        },
    ],
    SAE_J1739: [
        {
            stepNumber: 1, title: 'Planning', description: 'Define scope and assemble team', quickLinks: [
                { label: 'Project Setup', path: '/project-management', icon: 'Settings' },
            ]
        },
        {
            stepNumber: 2, title: 'Functional Analysis', description: 'Identify item functions and requirements', quickLinks: [
                { label: 'P-Diagrams', path: '/p-diagrams', icon: 'Network' },
                { label: 'Traceability', path: '/traceability', icon: 'GitBranch' },
            ]
        },
        {
            stepNumber: 3, title: 'Failure Analysis', description: 'Identify potential failure modes', quickLinks: [
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle' },
            ]
        },
        {
            stepNumber: 4, title: 'Risk Assessment', description: 'Rate severity, occurrence, detection', quickLinks: [
                { label: 'Risk Matrix', path: '/risk-matrix', icon: 'Grid' },
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle' },
            ]
        },
        {
            stepNumber: 5, title: 'Recommended Actions', description: 'Define risk reduction actions', quickLinks: [
                { label: 'Action Tracker', path: '/action-tracking', icon: 'CheckSquare' },
                { label: 'DVP&R', path: '/dvpr', icon: 'ClipboardCheck' },
            ]
        },
    ],
    ISO_14971: [
        {
            stepNumber: 1, title: 'Risk Analysis', description: 'Identify hazards and hazardous situations', quickLinks: [
                { label: 'P-Diagrams', path: '/p-diagram', icon: 'Network', description: 'Hazard identification' },
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle', description: 'Hazard analysis' },
            ]
        },
        {
            stepNumber: 2, title: 'Risk Evaluation', description: 'Evaluate risk acceptability', quickLinks: [
                { label: 'Risk Matrix', path: '/risk-analysis', icon: 'Grid', description: 'Severity × Probability' },
            ]
        },
        {
            stepNumber: 3, title: 'Risk Control', description: 'Implement risk control measures', quickLinks: [
                { label: 'Action Tracker', path: '/action-tracking', icon: 'CheckSquare' },
                { label: 'Controls', path: '/risk-analysis', icon: 'Shield' },
            ]
        },
        {
            stepNumber: 4, title: 'Residual Risk Evaluation', description: 'Assess risk after controls', quickLinks: [
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle', description: 'Re-evaluate' },
                { label: 'Risk Matrix', path: '/risk-analysis', icon: 'Grid' },
            ]
        },
        {
            stepNumber: 5, title: 'Risk Management Report', description: 'Document the complete process', quickLinks: [
                { label: 'Reports', path: '/export', icon: 'FileBarChart' },
                { label: 'Epoch History', path: '/epoch-manager', icon: 'History' },
            ]
        },
    ],
    MIL_STD_1629A: [
        {
            stepNumber: 1, title: 'Ground Rules & Assumptions', description: 'Define system boundaries and GR&A', quickLinks: [
                { label: 'Project Setup', path: '/projects', icon: 'Settings' },
                { label: 'Customer Reqs', path: '/customer-requirements', icon: 'FileText' },
            ]
        },
        {
            stepNumber: 2, title: 'System Block Diagram', description: 'Create functional block diagrams', quickLinks: [
                { label: 'P-Diagrams', path: '/p-diagram', icon: 'Network' },
                { label: 'BOM Structure', path: '/bom-functions', icon: 'List' },
            ]
        },
        {
            stepNumber: 3, title: 'Failure Mode Identification', description: 'Identify all potential failure modes', quickLinks: [
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle' },
            ]
        },
        {
            stepNumber: 4, title: 'Criticality Analysis', description: 'Calculate criticality numbers (Cm)', quickLinks: [
                { label: 'Risk Matrix', path: '/risk-analysis', icon: 'Grid' },
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle' },
            ]
        },
        {
            stepNumber: 5, title: 'Maintainability Analysis', description: 'Assess maintainability impact', quickLinks: [
                { label: 'DVP&R', path: '/dvpr', icon: 'ClipboardCheck' },
                { label: 'Action Tracker', path: '/action-tracking', icon: 'CheckSquare' },
            ]
        },
        {
            stepNumber: 6, title: 'FMECA Report', description: 'Generate MIL-STD compliant report', quickLinks: [
                { label: 'Reports', path: '/export', icon: 'FileBarChart' },
                { label: 'Epoch History', path: '/epoch-manager', icon: 'History' },
            ]
        },
    ],
    ISO_26262: [
        {
            stepNumber: 1, title: 'Hazard Analysis & Risk Assessment', description: 'HARA per ISO 26262-3', quickLinks: [
                { label: 'P-Diagrams', path: '/p-diagram', icon: 'Network' },
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle' },
            ]
        },
        {
            stepNumber: 2, title: 'Safety Goal Definition', description: 'Define top-level safety goals', quickLinks: [
                { label: 'Customer Reqs', path: '/customer-requirements', icon: 'FileText' },
                { label: 'Func. Safety', path: '/functional-safety', icon: 'Shield', description: 'Safety Goals' },
            ]
        },
        {
            stepNumber: 3, title: 'Functional Safety Concept', description: 'FSC development', quickLinks: [
                { label: 'P-Diagrams', path: '/p-diagram', icon: 'Network' },
                { label: 'Func. Safety', path: '/functional-safety', icon: 'Shield' },
            ]
        },
        {
            stepNumber: 4, title: 'Technical Safety Concept', description: 'TSC and safety mechanisms', quickLinks: [
                { label: 'Controls', path: '/risk-analysis', icon: 'Shield' },
                { label: 'Func. Safety', path: '/functional-safety', icon: 'Layers' },
            ]
        },
        {
            stepNumber: 5, title: 'System/HW/SW Design', description: 'Detailed design per ASIL', quickLinks: [
                { label: 'BOM Structure', path: '/bom-functions', icon: 'List' },
                { label: 'Hardware Metrics', path: '/functional-safety', icon: 'Activity' },
            ]
        },
        {
            stepNumber: 6, title: 'Verification & Validation', description: 'V&V activities per ISO 26262-4/8', quickLinks: [
                { label: 'DVP&R', path: '/dvpr', icon: 'ClipboardCheck' },
                { label: 'Test Results', path: '/dvpr', icon: 'FlaskConical' },
            ]
        },
        {
            stepNumber: 7, title: 'Safety Assessment', description: 'Functional Safety Assessment', quickLinks: [
                { label: 'Reports', path: '/export', icon: 'FileBarChart' },
                { label: 'Func. Safety', path: '/functional-safety', icon: 'Shield' },
            ]
        },
    ],
    IEC_61508: [
        {
            stepNumber: 1, title: 'Concept & Scope Definition', description: 'Define EUC and scope', quickLinks: [
                { label: 'Project Setup', path: '/projects', icon: 'Settings' },
            ]
        },
        {
            stepNumber: 2, title: 'Hazard & Risk Analysis', description: 'Identify hazards and consequences', quickLinks: [
                { label: 'P-Diagrams', path: '/p-diagram', icon: 'Network' },
                { label: 'HARA', path: '/functional-safety', icon: 'AlertTriangle' },
            ]
        },
        {
            stepNumber: 3, title: 'Safety Requirements Specification', description: 'Define SRS', quickLinks: [
                { label: 'Customer Reqs', path: '/customer-requirements', icon: 'FileText' },
                { label: 'Safety Goals', path: '/functional-safety', icon: 'Shield' },
            ]
        },
        {
            stepNumber: 4, title: 'SIL Determination', description: 'Determine required SIL', quickLinks: [
                { label: 'Risk Matrix', path: '/risk-analysis', icon: 'Grid' },
                { label: 'Func. Safety', path: '/functional-safety', icon: 'Activity' },
            ]
        },
        {
            stepNumber: 5, title: 'Design & Development', description: 'E/E/PE system design per SIL', quickLinks: [
                { label: 'BOM Structure', path: '/bom-functions', icon: 'List' },
                { label: 'Controls', path: '/risk-analysis', icon: 'Shield' },
            ]
        },
        {
            stepNumber: 6, title: 'Verification & Validation', description: 'V&V per IEC 61508-3', quickLinks: [
                { label: 'DVP&R', path: '/dvpr', icon: 'ClipboardCheck' },
                { label: 'Test Results', path: '/dvpr', icon: 'FlaskConical' },
            ]
        },
        {
            stepNumber: 7, title: 'Functional Safety Assessment', description: 'Independent FSA', quickLinks: [
                { label: 'Reports', path: '/export', icon: 'FileBarChart' },
            ]
        },
    ],
    CUSTOMER_CUSTOM: [
        {
            stepNumber: 1, title: 'Initiation', description: 'Project kickoff', quickLinks: [
                { label: 'Project Setup', path: '/projects', icon: 'Settings' },
            ]
        },
        {
            stepNumber: 2, title: 'Analysis', description: 'Perform failure analysis', quickLinks: [
                { label: 'FMEA Editor', path: '/fmeas', icon: 'AlertTriangle' },
                { label: 'Risk Matrix', path: '/risk-analysis', icon: 'Grid' },
            ]
        },
        {
            stepNumber: 3, title: 'Approval', description: 'Review and approval', quickLinks: [
                { label: 'Reports', path: '/export', icon: 'FileBarChart' },
            ]
        },
    ],
};

export const getWorkflowSteps = (standard?: FmeaStandard): WorkflowStep[] => {
    return STANDARD_WORKFLOWS[standard || 'AIAG_VDA'] || STANDARD_WORKFLOWS.AIAG_VDA;
};

export const getStandardConfig = (standard?: FmeaStandard): FmeaStandardConfig => {
    return STANDARDS_CONFIG[standard || 'AIAG_VDA'] || STANDARDS_CONFIG.AIAG_VDA;
};

/**
 * Get simplified risk matrix configuration for InteractiveRiskMatrix component
 */
export const getRiskMatrixConfig = (standard: FmeaStandard) => {
    const config = STANDARDS_CONFIG[standard];
    if (!config?.riskMatrix) return null;

    // Convert to simplified format expected by InteractiveRiskMatrix
    const severityLevels = config.riskMatrix.yAxis.scale.map((label, idx) => ({
        value: idx + 1,
        label
    }));

    const probabilityLevels = config.riskMatrix.xAxis.scale.map((label, idx) => ({
        value: idx + 1,
        label
    }));

    const riskLevels = config.riskMatrix.levels.map((level) => ({
        level: level.name,
        color: level.color,
        minSeverity: 1,
        maxSeverity: 10,
        minProbability: 1,
        maxProbability: 10
    }));

    return {
        severityLevels,
        probabilityLevels,
        riskLevels
    };
};

